import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabase';
import { Save, Shield, Bell, Globe, Database, Mail, Clock, Eye, EyeOff, AlertCircle, Check } from 'lucide-react';

interface SystemSettings {
  id?: string;
  assessmentTimeoutMinutes: number;
  maxFileUploadSizeMb: number;
  sessionTimeoutHours: number;
  passwordMinLength: number;
  passwordRequireUppercase: boolean;
  passwordRequireLowercase: boolean;
  passwordRequireNumbers: boolean;
  passwordRequireSpecial: boolean;
  enableTwoFactor: boolean;
  enableEmailNotifications: boolean;
  enableSmsNotifications: boolean;
  enableAuditLogging: boolean;
  dataRetentionDays: number;
  allowAnonymousAssessments: boolean;
  maintenanceMode: boolean;
  maintenanceMessage?: string;
  smtpHost?: string;
  smtpPort: number;
  smtpUser?: string;
  smtpPassword?: string;
  smtpFromEmail?: string;
  smtpFromName: string;
}

// Map between camelCase (UI) and snake_case (DB)
function mapFromDb(dbSettings: any): SystemSettings {
  return {
    id: dbSettings.id,
    assessmentTimeoutMinutes: dbSettings.assessment_timeout_minutes ?? 60,
    maxFileUploadSizeMb: dbSettings.max_file_upload_size_mb ?? 10,
    sessionTimeoutHours: dbSettings.session_timeout_hours ?? 24,
    passwordMinLength: dbSettings.password_min_length ?? 8,
    passwordRequireUppercase: dbSettings.password_require_uppercase ?? true,
    passwordRequireLowercase: dbSettings.password_require_lowercase ?? true,
    passwordRequireNumbers: dbSettings.password_require_numbers ?? true,
    passwordRequireSpecial: dbSettings.password_require_special ?? true,
    enableTwoFactor: dbSettings.enable_two_factor ?? false,
    enableEmailNotifications: dbSettings.enable_email_notifications ?? true,
    enableSmsNotifications: dbSettings.enable_sms_notifications ?? false,
    enableAuditLogging: dbSettings.enable_audit_logging ?? true,
    dataRetentionDays: dbSettings.data_retention_days ?? 365,
    allowAnonymousAssessments: dbSettings.allow_anonymous_assessments ?? false,
    maintenanceMode: dbSettings.maintenance_mode ?? false,
    maintenanceMessage: dbSettings.maintenance_message ?? '',
    smtpHost: dbSettings.smtp_host ?? '',
    smtpPort: dbSettings.smtp_port ?? 587,
    smtpUser: dbSettings.smtp_user ?? '',
    smtpPassword: dbSettings.smtp_password ?? '',
    smtpFromEmail: dbSettings.smtp_from_email ?? '',
    smtpFromName: dbSettings.smtp_from_name ?? 'Helsenøkkelen',
  };
}

function mapToDb(settings: SystemSettings): any {
  return {
    assessment_timeout_minutes: settings.assessmentTimeoutMinutes,
    max_file_upload_size_mb: settings.maxFileUploadSizeMb,
    session_timeout_hours: settings.sessionTimeoutHours,
    password_min_length: settings.passwordMinLength,
    password_require_uppercase: settings.passwordRequireUppercase,
    password_require_lowercase: settings.passwordRequireLowercase,
    password_require_numbers: settings.passwordRequireNumbers,
    password_require_special: settings.passwordRequireSpecial,
    enable_two_factor: settings.enableTwoFactor,
    enable_email_notifications: settings.enableEmailNotifications,
    enable_sms_notifications: settings.enableSmsNotifications,
    enable_audit_logging: settings.enableAuditLogging,
    data_retention_days: settings.dataRetentionDays,
    allow_anonymous_assessments: settings.allowAnonymousAssessments,
    maintenance_mode: settings.maintenanceMode,
    maintenance_message: settings.maintenanceMessage,
    smtp_host: settings.smtpHost,
    smtp_port: settings.smtpPort,
    smtp_user: settings.smtpUser,
    smtp_password: settings.smtpPassword,
    smtp_from_email: settings.smtpFromEmail,
    smtp_from_name: settings.smtpFromName,
  };
}

export function Settings() {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testingEmail, setTestingEmail] = useState(false);

  // Supabase query
  const { data: settingsData, loading: settingsLoading } = useSupabaseQuery<any>(
    'system_settings',
    { single: true }
  );

  // Local state for settings (initialized from Supabase data)
  const [settings, setSettings] = useState<SystemSettings>({
    assessmentTimeoutMinutes: 60,
    maxFileUploadSizeMb: 10,
    sessionTimeoutHours: 24,
    passwordMinLength: 8,
    passwordRequireUppercase: true,
    passwordRequireLowercase: true,
    passwordRequireNumbers: true,
    passwordRequireSpecial: true,
    enableTwoFactor: false,
    enableEmailNotifications: true,
    enableSmsNotifications: false,
    enableAuditLogging: true,
    dataRetentionDays: 365,
    allowAnonymousAssessments: false,
    maintenanceMode: false,
    maintenanceMessage: '',
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpFromEmail: '',
    smtpFromName: 'Helsenøkkelen'
  });

  // Update local state when Supabase data changes
  React.useEffect(() => {
    if (settingsData) {
      setSettings(mapFromDb(settingsData));
    }
  }, [settingsData]);

  const handleSave = async () => {
    try {
      setError(null);
      setSuccess(null);

      const dbData = mapToDb(settings);

      if (settings.id) {
        const { error: updateError } = await supabase
          .from('system_settings')
          .update(dbData)
          .eq('id', settings.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('system_settings')
          .insert(dbData);
        if (insertError) throw insertError;
      }

      setSuccess('Settings saved successfully');
    } catch (err: any) {
      console.error('Error saving settings:', err);
      setError(err.message || 'Failed to save settings');
    }
  };

  const testEmailSettings = async () => {
    try {
      setTestingEmail(true);
      setError(null);

      // For now, just show a message - actual email testing would need an edge function
      setSuccess('Test email functionality requires a server-side edge function.');
    } catch (err: any) {
      console.error('Error testing email:', err);
      setError(err.message || 'Failed to send test email. Please check your SMTP settings.');
    } finally {
      setTestingEmail(false);
    }
  };

  if (settingsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading settings...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', label: 'General', icon: Globe },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'data', label: 'Data & Privacy', icon: Database },
    { id: 'email', label: 'Email Configuration', icon: Mail },
    { id: 'maintenance', label: 'Maintenance', icon: Clock }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">System Settings</h1>
          <p className="mt-1 text-sm text-gray-600">
            Configure system-wide settings and preferences
          </p>
        </div>
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
        >
          <Save className="h-4 w-4" />
          Save Settings
        </button>
      </div>

      {(error || success) && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          error ? 'bg-red-50 text-red-700 dark:text-red-300' : 'bg-green-50 text-green-700'
        }`}>
          {error ? <AlertCircle className="h-5 w-5" /> : <Check className="h-5 w-5" />}
          {error || success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="border-b">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">General Settings</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assessment Timeout (minutes)
                  </label>
                  <input
                    type="number"
                    value={settings.assessmentTimeoutMinutes}
                    onChange={(e) => setSettings({
                      ...settings,
                      assessmentTimeoutMinutes: parseInt(e.target.value) || 60
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Time before an inactive assessment expires
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max File Upload Size (MB)
                  </label>
                  <input
                    type="number"
                    value={settings.maxFileUploadSizeMb}
                    onChange={(e) => setSettings({
                      ...settings,
                      maxFileUploadSizeMb: parseInt(e.target.value) || 10
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session Timeout (hours)
                  </label>
                  <input
                    type="number"
                    value={settings.sessionTimeoutHours}
                    onChange={(e) => setSettings({
                      ...settings,
                      sessionTimeoutHours: parseInt(e.target.value) || 24
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Time before users need to log in again
                  </p>
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.allowAnonymousAssessments}
                      onChange={(e) => setSettings({
                        ...settings,
                        allowAnonymousAssessments: e.target.checked
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Allow Anonymous Assessments
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Allow users to complete assessments without logging in
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Security Settings</h3>

              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700">Password Requirements</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minimum Password Length
                    </label>
                    <input
                      type="number"
                      min="6"
                      max="32"
                      value={settings.passwordMinLength}
                      onChange={(e) => setSettings({
                        ...settings,
                        passwordMinLength: parseInt(e.target.value) || 8
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings.passwordRequireUppercase}
                        onChange={(e) => setSettings({
                          ...settings,
                          passwordRequireUppercase: e.target.checked
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Require uppercase letters</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings.passwordRequireLowercase}
                        onChange={(e) => setSettings({
                          ...settings,
                          passwordRequireLowercase: e.target.checked
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Require lowercase letters</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings.passwordRequireNumbers}
                        onChange={(e) => setSettings({
                          ...settings,
                          passwordRequireNumbers: e.target.checked
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Require numbers</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={settings.passwordRequireSpecial}
                        onChange={(e) => setSettings({
                          ...settings,
                          passwordRequireSpecial: e.target.checked
                        })}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Require special characters</span>
                    </label>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.enableTwoFactor}
                      onChange={(e) => setSettings({
                        ...settings,
                        enableTwoFactor: e.target.checked
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Enable Two-Factor Authentication
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Require users to set up 2FA for enhanced security
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Settings</h3>

              <div className="space-y-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.enableEmailNotifications}
                    onChange={(e) => setSettings({
                      ...settings,
                      enableEmailNotifications: e.target.checked
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Enable Email Notifications
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.enableSmsNotifications}
                    onChange={(e) => setSettings({
                      ...settings,
                      enableSmsNotifications: e.target.checked
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Enable SMS Notifications
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.enableAuditLogging}
                    onChange={(e) => setSettings({
                      ...settings,
                      enableAuditLogging: e.target.checked
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Enable Audit Logging
                  </span>
                </label>
                <p className="text-xs text-gray-500 ml-6">
                  Track all user actions and system changes for compliance
                </p>
              </div>
            </div>
          )}

          {activeTab === 'data' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Data & Privacy Settings</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Retention Period (days)
                  </label>
                  <input
                    type="number"
                    value={settings.dataRetentionDays}
                    onChange={(e) => setSettings({
                      ...settings,
                      dataRetentionDays: parseInt(e.target.value) || 365
                    })}
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Automatically delete old assessment data after this period
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-medium">GDPR Compliance Notice</p>
                      <p className="mt-1">
                        Ensure your data retention policies comply with GDPR and local regulations.
                        Consider implementing automated data anonymization for older records.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'email' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Email Configuration</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Host
                  </label>
                  <input
                    type="text"
                    value={settings.smtpHost || ''}
                    onChange={(e) => setSettings({ ...settings, smtpHost: e.target.value })}
                    placeholder="smtp.example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Port
                  </label>
                  <input
                    type="number"
                    value={settings.smtpPort}
                    onChange={(e) => setSettings({
                      ...settings,
                      smtpPort: parseInt(e.target.value) || 587
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Username
                  </label>
                  <input
                    type="text"
                    value={settings.smtpUser || ''}
                    onChange={(e) => setSettings({ ...settings, smtpUser: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SMTP Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={settings.smtpPassword || ''}
                      onChange={(e) => setSettings({ ...settings, smtpPassword: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Email
                  </label>
                  <input
                    type="email"
                    value={settings.smtpFromEmail || ''}
                    onChange={(e) => setSettings({ ...settings, smtpFromEmail: e.target.value })}
                    placeholder="noreply@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Name
                  </label>
                  <input
                    type="text"
                    value={settings.smtpFromName}
                    onChange={(e) => setSettings({ ...settings, smtpFromName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="pt-4 border-t">
                <button
                  onClick={testEmailSettings}
                  disabled={testingEmail || !settings.smtpHost || !settings.smtpFromEmail}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-400 flex items-center gap-2"
                >
                  <Mail className="h-4 w-4" />
                  {testingEmail ? 'Sending Test Email...' : 'Send Test Email'}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'maintenance' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Maintenance Mode</h3>

              <div className="space-y-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.maintenanceMode}
                    onChange={(e) => setSettings({
                      ...settings,
                      maintenanceMode: e.target.checked
                    })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Enable Maintenance Mode
                  </span>
                </label>
                <p className="text-xs text-gray-500 ml-6">
                  When enabled, only administrators can access the system
                </p>

                {settings.maintenanceMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Maintenance Message
                    </label>
                    <textarea
                      value={settings.maintenanceMessage || ''}
                      onChange={(e) => setSettings({
                        ...settings,
                        maintenanceMessage: e.target.value
                      })}
                      rows={3}
                      placeholder="System is under maintenance. Please check back later."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This message will be displayed to users during maintenance
                    </p>
                  </div>
                )}

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 mr-2" />
                    <div className="text-sm text-orange-800">
                      <p className="font-medium">Warning</p>
                      <p className="mt-1">
                        Enabling maintenance mode will immediately prevent all non-admin users from accessing the system.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
